// Feature: MCRO001-F0028 (inherits MCRO001-F0026/F0012)
// Test: T-MCRO-028

import {
  createProportionalTimingMap,
  validateDemonstrationTimingMap,
} from './demonstrationTimingMap';

const exactBars = (authoredBars, revisionId) => (authoredBars || [])
  .filter((bar) => bar?.revisionId === revisionId
    && Number.isInteger(bar?.startCount)
    && Number.isInteger(bar?.endCount)
    && bar.endCount - bar.startCount === 7)
  .sort((left, right) => left.startCount - right.startCount);

export function retimeTimingMapRange(map, {
  startCount,
  endCount,
  sections,
  authoredBars,
  revisionId,
}) {
  const current = validateDemonstrationTimingMap(map, revisionId);
  return createProportionalTimingMap({
    ...current,
    startCount,
    endCount,
    sections,
    authoredBars: exactBars(authoredBars, revisionId),
    mediaStartMs: current.mediaStartMs,
    mediaEndMs: current.mediaEndMs,
  }, revisionId);
}

export function moveTimingMapEndpoint(map, endpoint, targetBar, context) {
  if (!targetBar || !['start', 'end'].includes(endpoint)) {
    throw new Error('Choose a complete authored eight-count endpoint.');
  }
  return retimeTimingMapRange(map, {
    ...context,
    startCount: endpoint === 'start' ? targetBar.startCount : map.startCount,
    endCount: endpoint === 'end' ? targetBar.endCount : map.endCount,
  });
}

export function shiftTimingMapWindow(map, barDelta, context) {
  if (!Number.isInteger(barDelta)) throw new Error('Move the demonstration by whole authored bars.');
  const bars = exactBars(context.authoredBars, context.revisionId);
  const startIndex = bars.findIndex((bar) => bar.startCount === map.startCount);
  const endIndex = bars.findIndex((bar) => bar.endCount === map.endCount);
  const nextStart = bars[startIndex + barDelta];
  const nextEnd = bars[endIndex + barDelta];
  if (startIndex < 0 || endIndex < startIndex || !nextStart || !nextEnd) {
    throw new Error('The shifted demonstration must stay on complete authored bars in this revision.');
  }
  return retimeTimingMapRange(map, {
    ...context,
    startCount: nextStart.startCount,
    endCount: nextEnd.endCount,
  });
}

// Feature: MCRO001-F0028 (owner report 2026-08-26: "I move the bar up and
// down and it takes 5 sec before it is updated" — instant, optimistic
// body-drag). A read-only, never-throwing preview of shiftTimingMapWindow's
// result: while a pointer drag is in progress this lets the rail render the
// SNAPPED (bar-aligned) target continuously, with zero server round-trip, by
// clamping an out-of-range delta to the nearest reachable window instead of
// rejecting it outright. The actual commit on release still goes through the
// strict, throwing shiftTimingMapWindow above, so an edit this preview
// clamped can still be legitimately rejected — the preview only ever governs
// what is drawn mid-gesture, never what is persisted.
export function previewShiftedWindow(map, barDelta, context) {
  if (!Number.isInteger(barDelta)) return null;
  const bars = exactBars(context.authoredBars, context.revisionId);
  const startIndex = bars.findIndex((bar) => bar.startCount === map.startCount);
  const endIndex = bars.findIndex((bar) => bar.endCount === map.endCount);
  if (startIndex < 0 || endIndex < startIndex || bars.length === 0) return null;
  const clampedDelta = Math.max(-startIndex, Math.min(bars.length - 1 - endIndex, barDelta));
  const nextStart = bars[startIndex + clampedDelta];
  const nextEnd = bars[endIndex + clampedDelta];
  return nextStart && nextEnd ? { startCount: nextStart.startCount, endCount: nextEnd.endCount } : null;
}

export function reorderPriority(priorityOrder, demonstrationId, direction) {
  if (![-1, 1].includes(direction)) throw new Error('Priority moves one position at a time.');
  const ids = [...priorityOrder];
  const index = ids.indexOf(demonstrationId);
  const target = index + direction;
  if (index < 0 || target < 0 || target >= ids.length) return ids;
  [ids[index], ids[target]] = [ids[target], ids[index]];
  return ids;
}

export function timingMapMutationRequest(candidate, timingMap, priorityOrder) {
  // F0026 currently authorizes this shared save path with the edited map's
  // optimistic version. Do not synthesize a Sheet-wide priority token: the
  // future owning boundary must expose one before this client can send it.
  if (!Number.isInteger(candidate?.expectedMapVersion) || candidate.expectedMapVersion < 0) {
    throw new Error('A current demonstration timing-map version is required to save this change.');
  }
  const request = {
    demonstrationId: candidate.demonstrationId,
    revision: { revisionId: candidate.revisionId },
    timingMap: validateDemonstrationTimingMap(timingMap, candidate.revisionId),
    priorityOrder: [...priorityOrder],
    expectedMapVersion: candidate.expectedMapVersion,
  };
  if (candidate.mediaId) request.mediaId = candidate.mediaId;
  if (candidate.relation) request.relation = { ...candidate.relation };
  if (candidate.renditionMutation) request.rendition = { ...candidate.renditionMutation };
  return request;
}
